import React, { useCallback, useContext } from 'react';
import { useResponsiveSize } from '@bingads-webui-react/with-responsive-size';
import { useFormService, FORM_EVENTS } from '@bingads-webui-react/form-core';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Button,
  mergeClasses,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { DrawerHeaderTitle } from '@fluentui/react-drawer';

const useTitleBarStyles = makeStyles({
  stack: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('14px', '24px'),
    ...shorthands.gap('8px'),
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightBase500,
    ...shorthands.margin('0px'),
  },
  'title-s': {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
  },
  'title-m': {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
  },
});

interface TitleBarProps {
  classes?: {
    root?: string;
  };
  title: string | React.ReactNode;
  hideCloseButton?: boolean;
}

export const TitleBar: React.FC<TitleBarProps> = (props) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useTitleBarStyles();
  const displayMode = useResponsiveSize();
  const { send } = useFormService();
  const onCancelClicked = useCallback(() => {
    send(FORM_EVENTS.CANCEL);
  }, [send]);

  if (!props.title) {
    return null;
  }

  return (
    <div className={mergeClasses('title-bar', classes.stack, props.classes?.root)}>
      <h1 className={mergeClasses('top-bar-title', classes.title, classes[`title-${displayMode}`])}>{props.title}</h1>
      {!props.hideCloseButton
        && (
          <Button
            className="title-bar-close-button"
            appearance="subtle"
            icon={<Dismiss20Regular />}
            onClick={onCancelClicked}
            aria-label={i18n.getString(_TL_('Close'))}
          />
        )}
    </div>
  );
};

export const DrawerTitleBar: React.FC<TitleBarProps> = (props) => {
  const i18n = useContext(I18nContext) as any;
  const { send } = useFormService();
  const onCancelClicked = useCallback(() => {
    send(FORM_EVENTS.CANCEL);
  }, [send]);

  if (!props.title) {
    return null;
  }

  return (
    <DrawerHeaderTitle
      className={mergeClasses('drawer-title-bar', props.classes?.root)}
      action={props.hideCloseButton ? undefined : (
        <Button
          className="title-bar-close-button"
          appearance="subtle"
          icon={<Dismiss20Regular />}
          onClick={onCancelClicked}
          aria-label={i18n.getString(_TL_('Close'))}
        />
      )}
    >
      {props.title}
    </DrawerHeaderTitle>
  );
};
